import { Recurrence } from '@core/interfaces/api';
import { format, parseISO } from 'date-fns';

// RRule frequency constants (matching rrule library)
const FREQ = {
  DAILY: 3,
  WEEKLY: 2,
  MONTHLY: 1,
};

// RRule weekday constants (matching rrule library)
const WEEKDAY = {
  MO: 0,
  TU: 1,
  WE: 2,
  TH: 3,
  FR: 4,
  SA: 5,
  SU: 6,
};

// Weekday names mapping for numeric values (0-6)
const WEEKDAY_NAMES_NUMERIC = {
  0: 'Monday',
  1: 'Tuesday',
  2: 'Wednesday',
  3: 'Thursday',
  4: 'Friday',
  5: 'Saturday',
  6: 'Sunday',
};

// Weekday short names mapping
const WEEKDAY_SHORT_NAMES = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
};

/**
 * Converts recurrence data to human-readable text
 * Similar to prettyPrintRecurrenceRecipe in RecurrenceService but for React components
 */
export function formatRecurrenceText(recurrence: Recurrence): string {
  if (!recurrence || !recurrence.freq) {
    return '';
  }

  let readableRule = '';

  // Determine base frequency
  if (recurrence.freq === FREQ.MONTHLY) {
    readableRule = 'month';
  } else if (recurrence.byweekday && recurrence.byweekday.length > 0) {
    readableRule = 'week';
  } else {
    readableRule = 'day';
  }

  // Add interval
  if (recurrence.interval) {
    switch (recurrence.interval) {
      case 1:
        readableRule = `every ${readableRule}`;
        break;
      case 2:
        readableRule = `every other ${readableRule}`;
        break;
      default:
        readableRule = `every ${recurrence.interval} ${readableRule}s`;
    }
  }

  // Add weekdays for weekly/monthly recurrence
  if (recurrence.byweekday && recurrence.byweekday.length > 0) {
    const dayNames = recurrence.byweekday.map((day: string) => {
      // Handle both numeric strings (e.g., "4") and letter codes (e.g., "MO")
      const numericDay = parseInt(day, 10);
      if (!isNaN(numericDay) && numericDay >= 0 && numericDay <= 6) {
        // Numeric format (0-6)
        return WEEKDAY_NAMES_NUMERIC[
          numericDay as keyof typeof WEEKDAY_NAMES_NUMERIC
        ];
      } else {
        // String format (e.g., "MO")
        return (
          WEEKDAY_SHORT_NAMES[day as keyof typeof WEEKDAY_SHORT_NAMES] || day
        );
      }
    });

    let daysText = '';
    if (dayNames.length === 1) {
      daysText = dayNames[0];
    } else if (dayNames.length === 2) {
      daysText = `${dayNames[0]} and ${dayNames[1]}`;
    } else {
      const lastDay = dayNames.pop();
      daysText = `${dayNames.join(', ')} and ${lastDay}`;
    }

    let datePosition = '';
    if (recurrence.freq === FREQ.MONTHLY && recurrence.nbyweekday) {
      switch (recurrence.nbyweekday) {
        case 1:
          datePosition = 'first';
          break;
        case 2:
          datePosition = 'second';
          break;
        case 3:
          datePosition = 'third';
          break;
        case 4:
          datePosition = 'fourth';
          break;
        case -1:
          datePosition = 'last';
          break;
        case -2:
          datePosition = 'second to last';
          break;
        case -3:
          datePosition = 'third to last';
          break;
        case -4:
          datePosition = 'fourth to last';
          break;
      }
    }

    readableRule = `${readableRule} on ${
      datePosition ? `the ${datePosition} ` : ''
    }${daysText}`;
  }

  // Add end condition
  if (recurrence.count) {
    readableRule = `${readableRule} for ${recurrence.count} occurrences`;
  } else if (recurrence.until) {
    try {
      const untilDate = parseISO(recurrence.until);
      readableRule = `${readableRule} until ${format(untilDate, 'M/d/yy')}`;
    } catch (error) {
      // If date parsing fails, just show the raw until value
      readableRule = `${readableRule} until ${recurrence.until}`;
    }
  }

  // Add exceptions
  if (recurrence.exdates && recurrence.exdates.length > 0) {
    const exceptionDates = recurrence.exdates
      .map((date) => {
        try {
          return format(parseISO(date), 'M/d/yy');
        } catch {
          return date;
        }
      })
      .join(', ');

    readableRule = `${readableRule} (except on ${exceptionDates})`;
  }

  return readableRule;
}
